import useDetectCountry from '@hooks/useDetectCountry'
import { useIdentifierSwap } from '@hooks/useIdentifierSwap'
import { IdentifierMode } from '@lib/utils/identifier'
import React, { useEffect, useRef } from 'react'
import PhoneInput from 'react-phone-input-2'

type CountryData = { dialCode?: string }

type Props = {
  /** Reports what the field has decided it is, so the caller can label it. */
  onIdentifierStateChange?: (state: 'neutral' | 'phone' | 'email') => void
  value: string
  mode: IdentifierMode
  onChange: (value: string, mode: IdentifierMode) => void
  onEnter?: () => void
  placeholder?: string
  autoFocus?: boolean
  inputId?: string
}

const INPUT_CLASS =
  'appearance-none border border-grey-500 rounded w-full py-2 px-4 text-grey leading-tight focus:outline-none'

/**
 * One field that accepts either a phone number or an email address.
 *
 * While the value looks like a phone number this renders the same
 * `react-phone-input-2` widget the phone-only forms have always used, so the
 * country flag, dial code and masks are untouched. The moment a character that
 * cannot belong to a phone number is typed or pasted, it swaps to a plain text
 * input; clearing the field swaps back.
 */
const IdentifierInput = ({
  value,
  mode,
  onChange,
  onEnter,
  placeholder = 'Phone number or email',
  autoFocus = false,
  inputId = 'identifier',
  onIdentifierStateChange,
}: Props) => {
  const { countryCode } = useDetectCountry({
    isLowerCase: true,
    isDeterminedViaSystemTimezoneOnly: true,
  })
  const {
    showPhoneWidget,
    identifierState,
    plainInputRef,
    swapHandlers,
    handlePlainChange,
    onPhoneWidgetFocus,
  } = useIdentifierSwap(mode, value, onChange, onEnter)

  // Plain until the value looks like a phone number - see useIdentifierSwap.
  // The label lives in the caller here, so the decision has to travel up.
  useEffect(() => {
    onIdentifierStateChange?.(identifierState)
  }, [identifierState, onIdentifierStateChange])

  // `react-phone-input-2` reads a changed `country` prop as the user picking
  // a different country: `UNSAFE_componentWillReceiveProps` takes either the
  // country branch or the value branch, never both, and the country branch
  // resets the field to that dial code and drops the number.
  //
  // `useDetectCountry` answers after mount, so a field that opens with a
  // number - one prefilled from `?ph=` - lost it the moment detection landed
  // and was left showing a bare "+91". Detection therefore only decides where
  // an empty field starts; once the widget holds a number, the country it
  // resolved from that number stands.
  const widgetCountryRef = useRef<string>()
  if (!showPhoneWidget) widgetCountryRef.current = countryCode

  if (!showPhoneWidget) {
    return (
      <input
        ref={plainInputRef}
        id={inputId}
        name={inputId}
        type="text"
        autoComplete="email"
        className={INPUT_CLASS}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={handlePlainChange}
        onKeyDown={(event) => event.key === 'Enter' && onEnter?.()}
      />
    )
  }

  return (
    <PhoneInput
      containerClass="font-sans text-base leading-tight"
      inputClass={`${INPUT_CLASS} pl-16`}
      country={widgetCountryRef.current}
      value={value}
      searchStyle={{ marginBottom: '2px' }}
      enableSearch
      // `countryCodeEditable` is deliberately left at its default of true. With
      // it false, `handleInput` returns early on any value that does not start
      // with the dial code - so selecting the number and deleting it was
      // swallowed outright, no state change and no `onChange`. The field could
      // not be cleared, and so could not be switched to an email once a number
      // had been typed. Editing the dial code is harmless here: this field
      // rests as a plain text input and only becomes a phone widget once the
      // value looks like a number.
      onChange={(
        nextValue: string,
        data: CountryData | Record<string, never>
      ) => {
        // Anything down to the dial code is as good as empty - return the
        // field to its resting input rather than leaving a bare "+91".
        //
        // Compared by digit count, not equality: deleting one character at a
        // time walks through "+919", "+91", "+9", "+", and only one of those
        // ever equalled the dial code, so the widget stayed put. Formatting
        // characters made exact matching unreliable anyway.
        const dialDigits = ((data as CountryData)?.dialCode ?? '').replace(
          /\D/g,
          ''
        )
        const valueDigits = nextValue.replace(/\D/g, '')
        // No dial code means the widget could not place the value in any
        // country, which is what happens once you delete below the dial code:
        // "+91 9" -> "9", and react-phone-input-2 hands back an empty `data`.
        // That is not a number anyone can call, so treat it as empty. Reading
        // it as "has a digit, keep it" was what stranded the flag over a box
        // that looked blank.
        const hasNumber =
          Boolean(dialDigits) && valueDigits.length > dialDigits.length
        onChange(hasNumber ? nextValue : '', 'phone')
      }}
      onFocus={onPhoneWidgetFocus}
      masks={{ in: '..... .....' }}
      inputProps={{
        id: inputId,
        name: inputId,
        required: true,
        // Always focus on mount: the widget has just replaced the input the
        // user was typing into, and its focus event carries the dial code
        // needed to finish the handoff.
        autoFocus: true,
        placeholder,
        ...swapHandlers,
      }}
    />
  )
}

export default IdentifierInput
